import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosRequestConfig, Canceler } from 'axios'
import { ElMessage } from 'element-plus'
import { useAuthStore } from '@/stores/auth'
import { toLocalISOString } from './format'
import i18n from '@/i18n'

// Extend AxiosRequestConfig to allow silent property
declare module 'axios' {
  interface AxiosRequestConfig {
    silent?: boolean
    /** V5-P1-11：跳过 GET 请求去重（用于需要强制刷新的场景） */
    skipDedup?: boolean
    /** 生产网络瞬时异常仅允许自动重试一次，防止形成重试风暴 */
    transientRetryAttempted?: boolean
  }
}

function convertDatesToLocalISO(obj: any): any {
  if (obj === null || obj === undefined) return obj
  if (obj instanceof Date) {
    return toLocalISOString(obj)
  }
  if (Array.isArray(obj)) return obj.map(convertDatesToLocalISO)
  if (typeof obj === 'object') {
    const result: any = {}
    for (const key of Object.keys(obj)) {
      result[key] = convertDatesToLocalISO(obj[key])
    }
    return result
  }
  return obj
}

const request: AxiosInstance = axios.create({
  baseURL: import.meta.env?.VITE_API_BASE_URL || '',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json'
  },
  transformRequest: [(data) => {
    return JSON.stringify(convertDatesToLocalISO(data))
  }]
})

/**
 * V5-P1-11：GET 请求去重 + AbortController
 *
 * 同一 method+url+params 的 GET 请求在飞行中时，复用已有 Promise，避免双击或 SSE+手动刷新触发两次后端调用。
 * 同时为每个请求创建 AbortController，组件 unmount 时可调用 cancelableRequest(config) 取消。
 */
const inflightRequests = new Map<string, { promise: Promise<any>, canceler: Canceler }>()

function buildRequestKey(config: InternalAxiosRequestConfig): string {
  const method = (config.method || 'get').toLowerCase()
  const url = config.url || ''
  const params = config.params ? JSON.stringify(config.params) : ''
  return `${method}:${url}:${params}`
}

function shouldDedup(config: InternalAxiosRequestConfig): boolean {
  const method = (config.method || 'get').toLowerCase()
  // 仅对 GET 做去重；写操作不去重（每次都需独立提交，由 :loading 防抖保护）
  if (method !== 'get') return false
  // 显式声明跳过去重的请求（如强制刷新）不去重
  if ((config as any).skipDedup) return false
  return true
}

request.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const authStore = useAuthStore()
    const token = authStore.token
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    config.headers['Accept-Language'] = i18n.global.locale.value
    // API GET 响应是动态业务数据，禁止浏览器 HTTP 缓存：
    // 旧版后端曾让浏览器缓存过空响应（code:"000"），导致时间轴等一直显示旧数据。
    // _t 时间戳改变请求 URL 绕过既有缓存；注意 GET 去重 key 基于原始 params 构建，不受 _t 影响。
    if ((config.method || 'get').toLowerCase() === 'get') {
      config.params = { ...(config.params || {}), _t: Date.now() }
      config.headers['Cache-Control'] = 'no-cache'
      config.headers['Pragma'] = 'no-cache'
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

/**
 * V5-P1-11：包装 GET 请求，加入 in-flight 去重。
 * 同一 key 的 GET 请求在飞行中时，第二次调用直接复用 Promise，避免重复打后端。
 * 失败时自动从 inflightRequests 中清理，不影响下次重试。
 */
const originalGet: AxiosInstance['get'] = request.get.bind(request)
const deduplicatedGet = <T = any>(url: string, config?: AxiosRequestConfig): Promise<T> => {
  const mergedConfig: InternalAxiosRequestConfig = {
    ...(config || {}),
    url,
    method: 'get'
  } as InternalAxiosRequestConfig

  if (!shouldDedup(mergedConfig)) {
    return originalGet<T, T>(url, config)
  }

  const key = buildRequestKey(mergedConfig)
  const existing = inflightRequests.get(key)
  if (existing) {
    return existing.promise as Promise<T>
  }

  const controller = new AbortController()
  const finalConfig: AxiosRequestConfig = {
    ...(config || {}),
    signal: controller.signal
  }
  const promise = originalGet<T, T>(url, finalConfig).finally(() => {
    inflightRequests.delete(key)
  })
  inflightRequests.set(key, { promise, canceler: controller.abort.bind(controller) })
  return promise
}
request.get = deduplicatedGet as AxiosInstance['get']

/**
 * V5-P1-11：取消所有飞行中的 GET 请求（用于路由切换、登出等场景）。
 */
export function cancelAllInflightRequests(): void {
  inflightRequests.forEach(({ canceler }) => {
    try { canceler() } catch (e) { /* ignore */ }
  })
  inflightRequests.clear()
}

request.interceptors.response.use(
  (response) => {
    // 后端返回统一格式 {code, msg, data}，提取data字段
    const resData = response.data
    if (resData && typeof resData === 'object' && 'data' in resData) {
      const businessCode = Number((resData as { code?: number }).code || 200)
      if (businessCode !== 200) {
        const message = (resData as { msg?: string }).msg || i18n.global.t('msg.error.requestFailed')
        // 部分业务接口以 HTTP 200 承载业务错误码。转换成标准 AxiosError，
        // 让调用方能够从 response.status/data 读取真实的 401/409/423 等错误。
        const businessResponse = { ...response, status: businessCode, data: resData }
        throw new AxiosError(
          message,
          (resData as { errorCode?: string }).errorCode,
          response.config,
          response.request,
          businessResponse
        )
      }
      return resData.data
    }
    return resData
  },
  async (error: AxiosError) => {
    const { response } = error
    const config = error.config as InternalAxiosRequestConfig & { silent?: boolean }
    const silent = config?.silent || config?.headers?.['X-Silent']
    const t = i18n.global.t

    // 生产环境的 HTTP 访问链路可能出现瞬时 502/503/504、连接中断，
    // 或在携带 Bearer token 时偶发返回 403。仅对幂等 GET 重试一次；
    // POST/PUT/DELETE 等写操作绝不自动重试，避免重复提交。
    const method = config?.method?.toLowerCase()
    const status = response?.status
    const hasAuthorization = Boolean(config?.headers?.Authorization)
    const isCanceled = error.code === AxiosError.ERR_CANCELED
    const isRetryableStatus = status === 502 || status === 503 || status === 504 || (status === 403 && hasAuthorization)
    const isRetryableNetworkError = !response && !isCanceled
    if (
      config &&
      method === 'get' &&
      !config.transientRetryAttempted &&
      (isRetryableStatus || isRetryableNetworkError)
    ) {
      config.transientRetryAttempted = true
      await new Promise(resolve => setTimeout(resolve, 250))
      return request.request(config)
    }

    if (response) {
      const status = response.status
      const errorData = response.data as { message?: string; msg?: string; errorType?: string }
      const message = errorData?.msg || errorData?.message || t('msg.error.requestFailed')
      const errorType = errorData?.errorType

      if (!silent) {
        switch (status) {
          case 400:
            if (errorData?.msg) {
              ElMessage.warning(message)
            } else {
              ElMessage.error(message || t('msg.error.paramError'))
            }
            break
          case 401:
            if (window.location.pathname !== '/login') {
              ElMessage.error(t('msg.error.loginExpired'))
              localStorage.removeItem('token')
              window.location.href = '/login'
            }
            break
          case 403:
            ElMessage.error(t('msg.error.noPermission'))
            break
          case 404:
            ElMessage.error(t('msg.error.notFound'))
            break
          case 409:
            // V5-P2-2：使用已提取的 message 变量（后端返回字段是 msg），原代码读取 errorData?.message 会丢失具体文案
            ElMessage.warning(message || t('msg.error.concurrencyConflict'))
            break
          case 422:
            if (errorType === 'PROFIT_VALIDATION_ERROR') {
              ElMessage.warning(message)
            } else {
              ElMessage.error(message)
            }
            break
          case 500:
            ElMessage.error(t('msg.error.serverError'))
            break
          default:
            ElMessage.error(message)
        }
      }

      return Promise.reject(new Error(message))
    } else {
      if (!silent) {
        ElMessage.error(t('msg.error.networkError'))
      }
      return Promise.reject(new Error(t('msg.error.networkError')))
    }
  }
)

export default request
